import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProductGridSkeleton from './ProductGridSkeleton';
import GrillaDeCarpetas, { type UnidadDeCarpeta } from './moodboards/GrillaDeCarpetas';
import VacioDeElementos, { CARPETAS_SUGERIDAS } from './elementosDeMarca/VacioDeElementos';
import VistaDeCarpetaDeElementos from './elementosDeMarca/VistaDeCarpetaDeElementos';
import { usePrepararElementos } from './elementosDeMarca/PrepararElemento';
import { conElementosIniciales } from '../pages/v2/elementosIniciales';
import {
  crearCarpetaDeElementos,
  eliminarCarpetaDeElementos,
  eliminarElemento,
  fetchCarpetasDeElementos,
  renombrarElemento,
  fetchElementos,
  pintarCarpetaDeElementos,
  renombrarCarpetaDeElementos,
  subirElemento,
  type CarpetaDeElementos,
  type ElementoDeMarca,
} from '../pages/v2/elementosDeMarcaApi';

export const UNIDAD_ELEMENTOS: UnidadDeCarpeta = { uno: 'elemento', varios: 'elementos', articulo: 'los' };

/**
 * Tab "Elementos de marca" de Mi marca — ADR-2026-10-19-elementos-de-marca.
 *
 * Carpetas con los logos, los elementos gráficos y las tipografías de la marca. Es el tab
 * de Estilos (`MiMarcaMoodboardsTab`) con otros datos: este componente es el ÚNICO que
 * habla con `elementosDeMarcaApi`, y la grilla y la vista de adentro sólo dibujan.
 *
 * A diferencia de Estilos lo ven todos los rubros: un logo es de cualquier marca.
 */
export const MiMarcaElementosTab = ({
  companyId,
  brandId,
  marcaResuelta,
}: {
  companyId: string | null;
  /** La marca activa. Las carpetas son de UNA marca: sin marca no hay dónde guardarlas. */
  brandId: string | null;
  marcaResuelta: boolean;
}) => {
  const [carpetas, setCarpetas] = useState<CarpetaDeElementos[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [abierta, setAbierta] = useState<CarpetaDeElementos | null>(null);
  const [elementos, setElementos] = useState<ElementoDeMarca[]>([]);
  const [cargandoElementos, setCargandoElementos] = useState(false);
  const [subiendo, setSubiendo] = useState(0);

  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelado) setUserId(data.user?.id ?? null);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  const avisar = (e: unknown, quePasaba: string) => {
    console.error(`[MiMarcaElementosTab] ${quePasaba}`, e);
    setError(e instanceof Error && e.message ? e.message : `No pudimos ${quePasaba}. Probá de nuevo.`);
  };

  const recargarCarpetas = useCallback(async () => {
    if (!companyId || !brandId) return;
    try {
      setCarpetas(await fetchCarpetasDeElementos({ companyId, brandId }));
    } catch (e) {
      avisar(e, 'cargar las carpetas');
    }
  }, [companyId, brandId]);

  useEffect(() => {
    if (!companyId || !marcaResuelta) return;
    // Cambiar de marca cierra la carpeta abierta: es de la otra.
    setAbierta(null);
    setElementos([]);
    if (!brandId) {
      setCarpetas([]);
      setCargando(false);
      return;
    }
    let cancelado = false;
    setCargando(true);
    // Una marca sin carpetas arranca con las tres de siempre, su logo y su tipografía
    // (ADR-2026-10-22-los-elementos-de-marca-arrancan-con-lo-de-la-marca).
    conElementosIniciales({ companyId, brandId }, () => fetchCarpetasDeElementos({ companyId, brandId }))
      .then((lista) => {
        if (!cancelado) setCarpetas(lista);
      })
      .catch((e) => {
        if (!cancelado) avisar(e, 'cargar las carpetas');
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId, brandId, marcaResuelta]);

  const abrir = async (carpeta: CarpetaDeElementos) => {
    setAbierta(carpeta);
    setElementos([]);
    setCargandoElementos(true);
    try {
      setElementos(await fetchElementos({ carpetaId: carpeta.id }));
    } catch (e) {
      avisar(e, 'cargar los elementos');
    } finally {
      setCargandoElementos(false);
    }
  };

  const volver = () => {
    setAbierta(null);
    setElementos([]);
    void recargarCarpetas();
  };

  const crear = async (nombre: string, color?: string) => {
    if (!companyId || !brandId) return null;
    const carpeta = await crearCarpetaDeElementos({ companyId, brandId, nombre, color, userId });
    setCarpetas((previas) => [carpeta, ...previas]);
    return carpeta;
  };

  const agregar = async (files: File[]) => {
    if (!companyId || !brandId || !abierta) return;
    setError(null);
    setSubiendo((n) => n + files.length);
    for (const file of files) {
      try {
        const el = await subirElemento({ companyId, brandId, carpeta: abierta, file });
        setElementos((previos) => [el, ...previos]);
      } catch (e) {
        avisar(e, `subir «${file.name}»`);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  };

  // Cada imagen pasa antes por la preparación (recortar, título); las tipografías
  // suben directo (ADR-2026-10-20-el-elemento-se-prepara-antes-de-subir).
  const { preparar, pantalla } = usePrepararElementos((files) => void agregar(files), setError);

  if (!companyId || cargando) {
    return (
      <ProductGridSkeleton
        count={6}
        gridClassName="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3 xl:grid-cols-4"
        tileAspectClassName="aspect-[4/3]"
        tileClassName="rounded-[12px]"
        withLabel
      />
    );
  }

  if (!brandId) {
    return <p className="py-10 text-center text-[14px] text-[rgb(var(--brand-black-rgb)_/_0.6)]">Elegí una marca para ver sus elementos.</p>;
  }

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-center gap-2.5 rounded-md border border-red-200 bg-red-50 p-3 text-[14px] text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <p className="min-w-0 flex-1">{error}</p>
          <button type="button" onClick={() => setError(null)} className="shrink-0 text-[13px] font-medium underline">
            Cerrar
          </button>
        </div>
      )}

      {pantalla}

      {abierta ? (
        <VistaDeCarpetaDeElementos
          carpeta={abierta}
          elementos={elementos}
          cargando={cargandoElementos}
          subiendo={subiendo}
          onVolver={volver}
          onAgregar={preparar}
          onRenombrar={async (el, titulo) => {
            // Optimista, como el nombre de una carpeta.
            const antes = elementos;
            setElementos((previos) => previos.map((x) => (x.id === el.id ? { ...x, nombre: titulo, titulo } : x)));
            try {
              await renombrarElemento(el.id, titulo);
            } catch (e) {
              setElementos(antes);
              avisar(e, 'guardar el nombre');
            }
          }}
          onEliminar={async (el) => {
            try {
              await eliminarElemento(el);
              setElementos((previos) => previos.filter((x) => x.id !== el.id));
            } catch (e) {
              avisar(e, 'eliminar el elemento');
            }
          }}
        />
      ) : carpetas.length === 0 ? (
        <VacioDeElementos
          onCrearSugeridas={async () => {
            try {
              // En orden inverso: la grilla muestra la más nueva primero, y así queda
              // Logos, Elementos gráficos, Tipografías.
              for (const c of [...CARPETAS_SUGERIDAS].reverse()) await crear(c.nombre, c.color);
            } catch (e) {
              avisar(e, 'crear las carpetas');
            }
          }}
          onCrear={async (nombre) => {
            try {
              const carpeta = await crear(nombre);
              if (carpeta) void abrir(carpeta);
            } catch (e) {
              avisar(e, 'crear la carpeta');
            }
          }}
        />
      ) : (
        <GrillaDeCarpetas
          carpetas={carpetas}
          unidad={UNIDAD_ELEMENTOS}
          onAbrir={abrir}
          onCrear={async (nombre) => {
            try {
              await crear(nombre);
            } catch (e) {
              avisar(e, 'crear la carpeta');
            }
          }}
          onRenombrar={async (id, nombre) => {
            const antes = carpetas;
            setCarpetas((previas) => previas.map((c) => (c.id === id ? { ...c, nombre } : c)));
            try {
              await renombrarCarpetaDeElementos(id, nombre);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el nombre');
            }
          }}
          onCambiarColor={async (id, color) => {
            const antes = carpetas;
            setCarpetas((previas) => previas.map((c) => (c.id === id ? { ...c, color } : c)));
            try {
              await pintarCarpetaDeElementos(id, color);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el color');
            }
          }}
          onEliminar={async (id) => {
            try {
              await eliminarCarpetaDeElementos({ carpetaId: id });
              setCarpetas((previas) => previas.filter((c) => c.id !== id));
            } catch (e) {
              avisar(e, 'eliminar la carpeta');
            }
          }}
        />
      )}
    </div>
  );
};

export default MiMarcaElementosTab;
